import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  Settings, 
  LogOut, 
  Network,
  User,
  ChevronRight,
  Globe,
  Wallet,
  ShieldAlert,
  Menu,
  X,
  Code
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

export const Layout = () => {
  const { user, logout, language, setLanguage, t, hasOpenRouterKey, openRouterBalance } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/settings', label: t('settings'), icon: Settings },
    { path: '/api-docs', label: 'API', icon: Code },
    ...(user?.role === 'super_admin' ? [
      { path: '/admin', label: language === 'it' ? 'Aziende' : 'Tenants', icon: ShieldAlert },
      { path: '/super-admin', label: 'Super Admin', icon: ShieldAlert }
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-8">
            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            
            <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3 group" data-testid="logo-link">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center group-hover:glow-primary transition-all">
                <Network className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <span className="font-heading font-semibold text-base sm:text-lg tracking-tight hidden xs:inline">
                LLM Council
              </span>
            </Link>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all
                      ${isActive 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* OpenRouter Balance */}
            {hasOpenRouterKey && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-success/10 border border-success/30 cursor-help">
                    <Wallet className="w-4 h-4 text-success" />
                    <span className="text-sm font-code text-success">
                      {openRouterBalance === null
                        ? '—'
                        : (language === 'it'
                          ? `€${(openRouterBalance * 0.92)?.toFixed(2)}`
                          : `$${openRouterBalance?.toFixed(2)}`)}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {openRouterBalance === null
                      ? (language === 'it' ? 'Credito OpenRouter non disponibile' : 'OpenRouter credit unavailable')
                      : (language === 'it' ? 'Credito OpenRouter residuo' : 'OpenRouter remaining credit')}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 sm:gap-2 px-2 sm:px-3" data-testid="language-selector">
                  <Globe className="w-4 h-4" />
                  <span className="uppercase font-code text-xs sm:text-sm">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('it')} data-testid="lang-it">
                  🇮🇹 Italiano
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en">
                  🇬🇧 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent/20 flex items-center justify-center">
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground hidden sm:inline" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-accent mt-1 font-code">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card/95 backdrop-blur-sm">
            <nav className="flex flex-col p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all
                      ${isActive 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                      }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
              
              {/* Mobile Balance */}
              {hasOpenRouterKey && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-success/10 border border-success/30">
                  <Wallet className="w-5 h-5 text-success" />
                  <span className="text-base font-code text-success">
                    {openRouterBalance === null
                      ? (language === 'it' ? 'Credito —' : 'Credit —')
                      : (language === 'it'
                        ? `€${(openRouterBalance * 0.92)?.toFixed(2)} credito`
                        : `$${openRouterBalance?.toFixed(2)} credit`)}
                  </span>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
